import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInputField } from "@/shared/components/field/input-field";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  Company,
  updateCompanyCache,
  usePatchCompanyMutation,
  useShowCompanyQuery,
} from "@/services/backend/company/api/company";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";

export function CompanyDataCard({ companyId }: { companyId: string }) {
  const { data, error, isLoading } = useShowCompanyQuery({ id: companyId });

  return (
    <div>
      {data && <CompanyDataCardContent company={data} />}
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
    </div>
  );
}

function CompanyDataCardContent({ company }: { company: Company }) {
  // The request is a partial patch, so each call passes the optimistic
  // cache value alongside it.
  const [set, { error }] = useOptimisticDebouncedMutation(
    { id: company.id },
    updateCompanyCache,
    usePatchCompanyMutation(),
    // All inputs debounce themselves, so the mutation does not have to.
    { delay: 0 },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Stammdaten")}</CardTitle>
        <CardDescription>
          {t("Der Name und die Kurzform des Unternehmens.")}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <TextInputField
          label={t("Name")}
          text={company.name}
          onTextChange={(name) =>
            set({ id: company.id, name }, { ...company, name })
          }
          errors={inputValidationErrorMessages(error, "name")}
        />
        <TextInputField
          label={t("Kurzform")}
          text={company.shortName}
          onTextChange={(shortName) =>
            set({ id: company.id, shortName }, { ...company, shortName })
          }
          errors={inputValidationErrorMessages(error, "shortName")}
        />
      </CardContent>
    </Card>
  );
}
